/*
 * Copyright (C) 2019 SADE Innovations Oy - All Rights Reserved
 *
 * NOTICE: This software is owned by SADE Innovations Oy and licensed under SADE Booster license.
 * All dissemination, usage, modification, copying, reproduction, selling and distribution of the
 * software and its intellectual and technical concepts are strictly forbidden without a valid license.
 * Such license can be obtained by issuing a SADE Booster License agreement from SADE Innovations Oy
 * (https://sadeinnovations.com).
 *
 */

import { Table, TableBody, TableCell, TableHead, TableRow } from "@material-ui/core";
import React, { Component, Fragment } from "react";
import Organization, { OrganizationObserver } from "../../../../data/organization/Organization";
import User from "../../../../data/organization/User";
import ConfirmationDialog from "../../../ui/confirmation-dialog";
import ErrorDialog from "../../../ui/error-dialog";
import Loader from "../../../ui/loader";
import NewUserForm from "./new-user-form";
import UserListItem from "./user-list-item";
import BackendFactory from "../../../../data/BackendFactory";
import PolicyGroup from "../../../../data/organization/PolicyGroup";
import { Maybe } from "../../../../types/aliases";
import { UserOrganizationAction } from "./user-organization-action";
import { translations } from "../../../../generated/translationHelper";

// current user's permissions within the context of the current organization
interface UserPermissions {
  deleteUser: boolean;
  removeUser: boolean;
  adjustPolicies: boolean;
}

const NO_PERMISSIONS: UserPermissions = {
  removeUser: false,
  deleteUser: false,
  adjustPolicies: false,
};

interface Props {
  organization: Organization;
  currentUserId: string;
}

interface State {
  loading: boolean;
  users: User[];
  action?: {user: User; action: UserOrganizationAction};
  userPermissions?: UserPermissions;
  policyGroups?: PolicyGroup[];
  errorMsg?: string;
}

export default class UserList extends Component<Props, State> implements OrganizationObserver {

  public constructor(props: Props) {
    super(props);
    this.state = {
      loading: false,
      users: [],
    };
  }

  public componentDidMount(): void {
    this.props.organization.addObserver(this);
    this.performUpdates();
  }

  public componentDidUpdate(prevProps: Readonly<Props>): void {
    if (prevProps.organization.getId() !== this.props.organization.getId()) {
      prevProps.organization.removeObserver(this);
      this.performUpdates();
      this.props.organization.addObserver(this);
    }
  }

  public componentWillUnmount(): void {
    this.props.organization.removeObserver(this);
  }

  private performUpdates(): void {
    this.setState({
      loading: true,
      userPermissions: undefined,
      policyGroups: undefined,
      users: [],
    });
    Promise
      .allSettled([
        this.updateUserPermissions(),
        this.updateUsers(),
        this.updatePolicyGroups(),
      ])
      .finally(() => {
        this.setState({ loading: false });
      });
  }

  private async updateUserPermissions(): Promise<void> {
    try {
      const user = await BackendFactory.getOrganizationBackend().getCurrentUser();

      if (user) {
        const organizationId = this.props.organization.getId();
        const allowedActions = await Promise.all([
          user.hasGrants(organizationId, "usersDelete"),
          user.hasGrants(organizationId, "organizationsUsersRemove"),
          user.hasGrants(organizationId, "policyGroupsUsersAdd", "policyGroupsUsersRemove"),
        ]);

        this.setState({
          userPermissions: {
            deleteUser: allowedActions[0],
            removeUser: allowedActions[1],
            adjustPolicies: allowedActions[2],
          },
        });
      }
    } catch (err) {
      console.error("updateUserPermissions", err);
      this.setState({ userPermissions: NO_PERMISSIONS });
    }
  }

  private async updateUsers(): Promise<void> {
    try {
      const users = await this.props.organization.getUsers();
      this.setState({
        users,
      });
    } catch (err) {
      console.error("updateUsers", err);
      this.setState({
        users: [],
      });
    }
  }

  private async updatePolicyGroups(): Promise<void> {
    try {
      const policyGroups = await this.props.organization.getPolicyGroups();
      policyGroups.sort(PolicyGroup.compareByPolicies);
      this.setState({ policyGroups });
    } catch (err) {
      console.error("updatePolicyGroups", err);
      this.setState({ policyGroups: [] });
    }
  }

  public onUsersChange(usersList: User[]): void {
    this.setState({
      users: usersList,
      loading: false,
    });
  }

  private handleUserAction = (action: UserOrganizationAction, user: User): void => {
    this.setState({
      action: {
        user,
        action,
      },
    });
  };

  private handleSubmitAction = async (): Promise<void> => {
    const action = this.state.action;

    if (!action) {
      return;
    }

    this.setState({ loading: true, action: undefined });

    try {
      if (action.action === UserOrganizationAction.RemoveFromOrganization) {
        await this.props.organization.removeUser(action.user);
      } else if (action.action === UserOrganizationAction.DeleteUser) {
        await action.user.delete();
        this.setState({ loading: false });
      }
    } catch (error) {
      this.setState({
        loading: false,
        errorMsg: translations.admin.texts.failedToDeleteUser(),
      });
    }
  };

  private handleCancelAction = (): void => {
    this.setState({ action: undefined });
  };

  private handleCloseErrorNote = (): void => {
    this.setState({ errorMsg: undefined });
  };

  private getUsersTableItems(): JSX.Element | JSX.Element[] {
    const users = this.state.users;
    const permissions = this.state.userPermissions;
    const organizationPolicies = this.state.policyGroups;

    const getUserAction = (user: User): UserOrganizationAction => {
      if (user.getId() === this.props.currentUserId) {
        return UserOrganizationAction.Nothing;
      } else if (user.getHomeOrganizationId() === this.props.organization.getId()) {
        return permissions?.deleteUser ? UserOrganizationAction.DeleteUser : UserOrganizationAction.Nothing;
      } else {
        return permissions?.removeUser ? UserOrganizationAction.RemoveFromOrganization : UserOrganizationAction.Nothing;
      }
    };

    if (organizationPolicies && permissions && users) {
      return users.map((user: User, index: number) => {
        return (
          <UserListItem
            key={user.getId()}
            user={user}
            userAction={getUserAction(user)}
            organizationId={this.props.organization.getId()}
            availablePolicyGroups={organizationPolicies}
            actionCallback={this.handleUserAction}
            enablePolicySelection={user.getId() !== this.props.currentUserId && permissions.adjustPolicies}
            data-testid={`user-${index}`}
          />
        );
      });
    } else {
      return (
        <tr>
          <td className="users-list-empty-text">{translations.admin.texts.noUsers()}</td>
        </tr>
      );
    }
  }

  private renderUsersList = (): JSX.Element | JSX.Element[] => {
    if (this.state.loading) {
      return <Loader/>;
    } else if (this.state.errorMsg) {
      return (
        <ErrorDialog
          errorMsg={this.state.errorMsg}
          onClose={this.handleCloseErrorNote}
        />
      );
    } else {
      return (
        <Table className="org-users-table" data-testid="users-table">
          <TableHead>
            <TableRow>
              <TableCell>{translations.common.texts.username()}</TableCell>
              <TableCell align="center">{translations.admin.texts.policyGroup()}</TableCell>
              <TableCell/>
            </TableRow>
          </TableHead>
          <TableBody>
            {this.getUsersTableItems()}
          </TableBody>
        </Table>
      );
    }
  };

  private renderActionPopup(): Maybe<JSX.Element> {
    if (!this.state.action) {
      return;
    }
    const title = this.state.action.action === UserOrganizationAction.DeleteUser
      ? translations.admin.texts.deleteUser()
      : this.state.action.action === UserOrganizationAction.RemoveFromOrganization
        ? translations.admin.texts.removeUser()
        : undefined;

    if (!title) {
      throw new Error("Unknown action selected!");
    }

    if (this.state.action) {
      return (
        <ConfirmationDialog
          title={title}
          message={this.state.action.user.getUsername()}
          onConfirm={this.handleSubmitAction}
          onCancel={this.handleCancelAction}
        />
      );
    }
  }

  public render(): JSX.Element {
    return (
      <Fragment>
        {
          this.state.policyGroups &&
          <NewUserForm
            showAccessError={false}
            showLoader={false}
            contextOrganization={this.props.organization}
            organization={this.props.organization}
            availablePolicyGroups={this.state.policyGroups}
          />
        }
        {this.renderUsersList()}
        {this.renderActionPopup()}
      </Fragment>
    );
  }
}
